class MDiscountCodesComponent extends HTMLElement {
  // Flip to true to resume auto-adding the birthday gift when a qualifying BDAY code is applied.
  static BDAY_GIFT_ENABLED = false;
  static BDAY_GIFT_VARIANT_ID = '62085028741388';
  static BDAY_GIFT_MIN_TOTAL_EUR = 150;
  static BDAY_GIFT_CODES = ['BDAY35', 'BDAYROUTINE', 'BDAYROUTINE30', 'BDAYSALE30', 'BDAYDEAL', 'VIPBDAY35'];

  // BDAY-LIVE35 adds a free product once the cart (excluding that gift) reaches €120.
  static LIVE_GIFT_ENABLED = true;
  static LIVE_GIFT_VARIANT_ID = '65962066510092';
  static LIVE_GIFT_MIN_TOTAL_EUR = 120;
  static LIVE_GIFT_CODE = 'BDAY-LIVE35';

  // Gift codes handled by the theme (Fall Sale 2026) — only while the Fall Sale runs
  // (window.FallSale.active, dates in snippets/fall-sale-head.liquid). Outside that window the
  // code is passed to Shopify like any other code and no gift is added.
  // The code is stored as a cart attribute (visible on the order as "Geschenk-Code"), the chip
  // is rendered from that attribute (snippets/cart-discount-codes.liquid) and the 0 € gift
  // variant is added once the cart total (after discounts, excluding the gift) reaches
  // minTotal — subscriptions and one-time purchases alike (oneTimeOnly: false).
  // While the gift is in the cart the code is also put on the Shopify cart (free shipping code
  // in the admin, one-time + subscription, min. 99 €), so the checkout shows the code tag.
  // If Shopify rejects it (e.g. before its start date) it is taken off again and not retried
  // until the cart changes — the gift itself is not affected.
  static CODE_GIFTS = [
    {
      code: 'BACSTAGE-GIFT',
      variantId: '45521226137868', // Geschenk – Inner Power+ (SKU 1305419), 0 €
      minTotal: 99,
      oneTimeOnly: false,
      property: '_isBacstageGift',
      attribute: 'Geschenk-Code',
    },
  ];

  constructor() {
    super();
    this.storeApiToken = window.storefrontApiToken;
    this.apiUrl = window.shopUrl + '/api/2024-10/graphql.json';
    this.cartToken = this.getCookie('cart');
    this.cartId = `gid://shopify/Cart/${this.cartToken}`;
  }

  connectedCallback() {
    this.networkCallObserver(this.requestCallback.bind(this));
    this.addEventListeners();

    let discount = this.querySelector('#discountOnly').dataset.value.replace(/(<([^>]+)>)/ig, '');;
    this.setAttribute('data-discount', discount);

    // Global listener (not tied to this element) — drawer section swaps remount this node.
    MDiscountCodesComponent.ensureBirthdayGiftCartSync();
    // Code may already be on the cart when the drawer mounts; sync once.
    MDiscountCodesComponent.scheduleBirthdayGiftSync();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  static isBirthdayGiftCode(code) {
    if (!code) return false;
    return MDiscountCodesComponent.BDAY_GIFT_CODES.includes(String(code).toUpperCase());
  }

  static isLiveGiftCode(code) {
    if (!code) return false;
    return String(code).toUpperCase() === MDiscountCodesComponent.LIVE_GIFT_CODE;
  }

  static isLiveGiftLine(item) {
    if (!item) return false;
    const props = item.properties || {};
    return (
      String(item.variant_id) === String(MDiscountCodesComponent.LIVE_GIFT_VARIANT_ID) ||
      props._isLiveGift === true ||
      props._isLiveGift === 'true'
    );
  }

  /** Cart total in € excluding the birthday gift line (so the free gift doesn't inflate the threshold). */
  static cartTotalExcludingBirthdayGift(cart) {
    if (!cart) return 0;
    const giftId = String(MDiscountCodesComponent.BDAY_GIFT_VARIANT_ID);
    let cents = cart.total_price || 0;
    for (const item of cart.items || []) {
      const props = item.properties || {};
      const isBirthdayGift =
        String(item.variant_id) === giftId ||
        props._isBirthdayGift === true ||
        props._isBirthdayGift === 'true';
      if (!isBirthdayGift) continue;
      cents -= item.final_line_price != null ? item.final_line_price : item.line_price || 0;
    }
    return cents / 100;
  }

  static birthdayGiftInCart(cart) {
    const giftId = String(MDiscountCodesComponent.BDAY_GIFT_VARIANT_ID);
    return (cart?.items || []).some((item) => {
      if (String(item.variant_id) === giftId) return true;
      const props = item.properties || {};
      return props._isBirthdayGift === true || props._isBirthdayGift === 'true';
    });
  }

  /** Cart total in € excluding the live gift line (so the free product doesn't inflate the €120 threshold). */
  static cartTotalExcludingLiveGift(cart) {
    if (!cart) return 0;
    let cents = cart.total_price || 0;
    for (const item of cart.items || []) {
      if (!MDiscountCodesComponent.isLiveGiftLine(item)) continue;
      cents -= item.final_line_price != null ? item.final_line_price : item.line_price || 0;
    }
    return cents / 100;
  }

  static liveGiftInCart(cart) {
    return (cart?.items || []).some((item) => MDiscountCodesComponent.isLiveGiftLine(item));
  }

  static collectAppliedDiscountCodeStrings(cart) {
    const codes = new Set();

    (cart?.discount_codes || []).forEach((entry) => {
      const code = typeof entry === 'string' ? entry : entry?.code;
      const applicable = typeof entry === 'object' ? entry.applicable !== false : true;
      if (applicable && code) codes.add(String(code).toUpperCase());
    });

    document.querySelectorAll('.js-remove-discount-button').forEach((el) => {
      if (el.dataset.discountCode) codes.add(String(el.dataset.discountCode).toUpperCase());
    });

    try {
      const cookieMatch = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
      if (cookieMatch && cookieMatch[1]) {
        decodeURIComponent(cookieMatch[1])
          .split(',')
          .forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) codes.add(trimmed.toUpperCase());
          });
      }
    } catch (e) {}

    try {
      const saved = sessionStorage.getItem('cart-checkout-discount-codes');
      if (saved) {
        JSON.parse(saved).forEach((code) => {
          if (code) codes.add(String(code).toUpperCase());
        });
      }
    } catch (e) {}

    return [...codes];
  }

  // ---------------------------------------------------------------- code gifts
  /** Gift codes only work while the Fall Sale is running. */
  static giftCodesLive() {
    return !!(window.FallSale && window.FallSale.active);
  }

  static codeGiftFor(code) {
    if (!code || !MDiscountCodesComponent.giftCodesLive()) return null;
    const upper = String(code).trim().toUpperCase();
    return MDiscountCodesComponent.CODE_GIFTS.find((gift) => gift.code === upper) || null;
  }

  static withoutGiftCodes(codes) {
    return (codes || []).filter((code) => code && !MDiscountCodesComponent.codeGiftFor(code));
  }

  static isCodeGiftLine(item, gift) {
    if (!item || !gift) return false;
    const props = item.properties || {};
    if (props[gift.property] === true || props[gift.property] === 'true') return true;
    // Same 0 € gift variant added without our property (but never the MOV gift line).
    return (
      String(item.variant_id) === String(gift.variantId) &&
      Number(item.final_line_price || 0) === 0 &&
      props._isMov == null
    );
  }

  /** A paid one-time purchase line (no subscription, not a free gift). */
  static isOneTimeLine(item, gift) {
    if (!item || item.selling_plan_allocation) return false;
    if (gift && MDiscountCodesComponent.isCodeGiftLine(item, gift)) return false;
    const props = item.properties || {};
    if (props._isMov != null || props._isBirthdayGift || props._isLiveGift) return false;
    return Number(item.original_line_price != null ? item.original_line_price : item.line_price || 0) > 0;
  }

  static cartHasOneTimeItem(cart, gift) {
    return (cart?.items || []).some((item) => MDiscountCodesComponent.isOneTimeLine(item, gift));
  }

  static giftConditionsMet(cart, gift) {
    if (!cart || !gift) return false;
    if (gift.oneTimeOnly && !MDiscountCodesComponent.cartHasOneTimeItem(cart, gift)) return false;
    return MDiscountCodesComponent.cartTotalExcludingCodeGift(cart, gift) >= gift.minTotal;
  }

  static cartHasGiftCode(cart, gift) {
    const attrs = (cart && cart.attributes) || {};
    return String(attrs[gift.attribute] || '').toUpperCase() === gift.code;
  }

  static shopifyCartCodes(cart) {
    return (cart?.discount_codes || [])
      .map((entry) => ({
        code: String((typeof entry === 'string' ? entry : entry?.code) || '').toUpperCase(),
        applicable: typeof entry === 'object' ? entry.applicable !== false : true,
      }))
      .filter((entry) => entry.code);
  }

  static async setShopifyCartCodes(codes) {
    const url = (window.routes && routes.cart_update_url ? routes.cart_update_url : '/cart/update') + '.js';
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...new Set(codes)].join(',') }),
    });
    return response.json();
  }

  /** Cart contents fingerprint (items + total), used to avoid retrying a rejected code on the same cart. */
  static cartSignature(cart) {
    return (
      String(cart?.total_price || 0) +
      '|' +
      (cart?.items || []).map((item) => item.key + 'x' + item.quantity).join(',')
    );
  }

  /**
   * Keeps the gift code on the Shopify cart while the gift is in the cart (so checkout shows
   * the code tag) and takes it off otherwise. Returns true when the cart changed.
   */
  static async syncGiftCodeOnShopify(cart, gift, giftInCart) {
    const blockKey = 'fallSale:giftCodeRejected:' + gift.code;
    const entries = MDiscountCodesComponent.shopifyCartCodes(cart);
    const onShopify = entries.find((entry) => entry.code === gift.code);
    const others = entries.filter((entry) => entry.code !== gift.code).map((entry) => entry.code);
    const signature = MDiscountCodesComponent.cartSignature(cart);
    let blocked = false;
    try {
      blocked = sessionStorage.getItem(blockKey) === signature;
    } catch (e) {}

    if (giftInCart && !onShopify && !blocked) {
      const next = await MDiscountCodesComponent.setShopifyCartCodes([...others, gift.code]);
      const added = MDiscountCodesComponent.shopifyCartCodes(next).find((entry) => entry.code === gift.code);
      if (!added || !added.applicable) {
        try {
          sessionStorage.setItem(blockKey, signature);
        } catch (e) {}
        await MDiscountCodesComponent.setShopifyCartCodes(others);
      }
      return true;
    }
    if (onShopify && (!giftInCart || !onShopify.applicable)) {
      await MDiscountCodesComponent.setShopifyCartCodes(others);
      return true;
    }
    return false;
  }

  static cartTotalExcludingCodeGift(cart, gift) {
    if (!cart) return 0;
    let cents = cart.total_price || 0;
    for (const item of cart.items || []) {
      if (!MDiscountCodesComponent.isCodeGiftLine(item, gift)) continue;
      cents -= item.final_line_price != null ? item.final_line_price : item.line_price || 0;
    }
    return cents / 100;
  }

  static async setGiftCodeAttribute(gift, on) {
    const attributes = {};
    attributes[gift.attribute] = on ? gift.code : '';
    const url = (window.routes && routes.cart_update_url ? routes.cart_update_url : '/cart/update') + '.js';
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
  }

  /** Adds / removes the code gifts. Returns true when the cart was changed. */
  static async syncCodeGiftsOnCart(cart) {
    if (!cart || !MDiscountCodesComponent.CODE_GIFTS.length) return false;
    const live = MDiscountCodesComponent.giftCodesLive();

    let mutated = false;
    for (const gift of MDiscountCodesComponent.CODE_GIFTS) {
      const hasCode = MDiscountCodesComponent.cartHasGiftCode(cart, gift);
      const eligible = live && hasCode && MDiscountCodesComponent.giftConditionsMet(cart, gift);
      const giftLines = (cart.items || []).filter((item) => MDiscountCodesComponent.isCodeGiftLine(item, gift));

      if (eligible && !giftLines.length) {
        const props = {};
        props[gift.property] = true;
        await AjaxCart.addToCart(
          JSON.stringify({ id: gift.variantId, quantity: 1, properties: props }),
          'code-gift'
        );
        mutated = true;
      } else if (eligible && (giftLines.length > 1 || (giftLines[0] && giftLines[0].quantity > 1))) {
        // Keep exactly one gift.
        for (let i = 0; i < giftLines.length; i++) {
          await AjaxCart.changeCart(
            JSON.stringify({ id: giftLines[i].key, quantity: i === 0 ? 1 : 0 }),
            'code-gift'
          );
        }
        mutated = true;
      } else if (!eligible && giftLines.length) {
        for (const line of giftLines) {
          await AjaxCart.changeCart(JSON.stringify({ id: line.key, quantity: 0 }), 'code-gift');
        }
        mutated = true;
      }

      // Code tag on the Shopify cart / checkout follows the gift.
      if (live) {
        const fresh = mutated ? await AjaxCart.getCart() : cart;
        if (fresh && (await MDiscountCodesComponent.syncGiftCodeOnShopify(fresh, gift, eligible))) mutated = true;
      }
    }
    return mutated;
  }

  static cartHasApplicableBirthdayGiftCode(cart) {
    return MDiscountCodesComponent.collectAppliedDiscountCodeStrings(cart).some((code) =>
      MDiscountCodesComponent.isBirthdayGiftCode(code)
    );
  }

  static ensureBirthdayGiftCartSync() {
    if (MDiscountCodesComponent._bdayGiftSyncBound) return;
    if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS?.cartUpdate) return;

    MDiscountCodesComponent._bdayGiftSyncBound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && (event.source === 'bday-gift' || event.source === 'live-gift' || event.source === 'code-gift' || event.source === 'mov')) return;
      MDiscountCodesComponent.scheduleBirthdayGiftSync();
    });
  }

  static scheduleBirthdayGiftSync() {
    clearTimeout(MDiscountCodesComponent._bdayGiftSyncTimer);
    // Short delay so /cart.js + drawer chips reflect the latest discounts/totals.
    MDiscountCodesComponent._bdayGiftSyncTimer = setTimeout(() => {
      MDiscountCodesComponent.syncBirthdayGiftForCart();
    }, 350);
  }

  static async syncBirthdayGiftForCart(options = {}) {
    if (typeof AjaxCart === 'undefined') return;
    if (MDiscountCodesComponent._bdayGiftSyncInFlight) {
      MDiscountCodesComponent._bdayGiftSyncQueued = true;
      return;
    }

    MDiscountCodesComponent._bdayGiftSyncInFlight = true;
    try {
      const cart = await AjaxCart.getCart();
      if (!cart) return;

      const hasBdayCode =
        MDiscountCodesComponent.BDAY_GIFT_ENABLED &&
        (options.forceCodeEligible === true ||
          MDiscountCodesComponent.cartHasApplicableBirthdayGiftCode(cart));
      const eligible =
        hasBdayCode &&
        MDiscountCodesComponent.cartTotalExcludingBirthdayGift(cart) >=
          MDiscountCodesComponent.BDAY_GIFT_MIN_TOTAL_EUR;
      const hasGift = MDiscountCodesComponent.birthdayGiftInCart(cart);
      const giftId = MDiscountCodesComponent.BDAY_GIFT_VARIANT_ID;

      let birthdayMutated = false;
      if (eligible && !hasGift) {
        await AjaxCart.addToCart(
          JSON.stringify({
            id: giftId,
            quantity: 1,
            properties: { _isBirthdayGift: true },
          }),
          'bday-gift'
        );
        birthdayMutated = true;
      } else if (!eligible && hasGift) {
        // Remove when under €150 or when no BDAY gift code remains on the cart.
        const giftItem = (cart.items || []).find((item) => {
          const props = item.properties || {};
          return (
            String(item.variant_id) === String(giftId) ||
            props._isBirthdayGift === true ||
            props._isBirthdayGift === 'true'
          );
        });
        await AjaxCart.changeCart(
          JSON.stringify({ id: giftItem?.key || giftId, quantity: 0 }),
          'bday-gift'
        );
        birthdayMutated = true;
      }

      if (MDiscountCodesComponent.LIVE_GIFT_ENABLED) {
        const liveCart = birthdayMutated ? await AjaxCart.getCart() : cart;
        if (liveCart) await MDiscountCodesComponent.syncLiveGiftOnCart(liveCart, options);
      }

      // Code gifts (BACSTAGE-GIFT …) — always on fresh cart data.
      const giftCart = await AjaxCart.getCart();
      if (giftCart) {
        const changed = await MDiscountCodesComponent.syncCodeGiftsOnCart(giftCart);
        if (changed && !options.skipDrawerRefresh) MDiscountCodesComponent.refreshDrawerAfterGift();
      }
    } catch (e) {
      console.error('syncBirthdayGiftForCart:', e);
    } finally {
      MDiscountCodesComponent._bdayGiftSyncInFlight = false;
      if (MDiscountCodesComponent._bdayGiftSyncQueued) {
        MDiscountCodesComponent._bdayGiftSyncQueued = false;
        MDiscountCodesComponent.scheduleBirthdayGiftSync();
      }
    }
  }

  static async syncLiveGiftOnCart(cart, options = {}) {
    const hasCode =
      options.forceLiveCodeEligible === true ||
      MDiscountCodesComponent.collectAppliedDiscountCodeStrings(cart).some((code) =>
        MDiscountCodesComponent.isLiveGiftCode(code)
      );
    const eligible =
      hasCode &&
      MDiscountCodesComponent.cartTotalExcludingLiveGift(cart) >=
        MDiscountCodesComponent.LIVE_GIFT_MIN_TOTAL_EUR;
    const hasGift = MDiscountCodesComponent.liveGiftInCart(cart);
    const giftId = MDiscountCodesComponent.LIVE_GIFT_VARIANT_ID;

    if (eligible && !hasGift) {
      await AjaxCart.addToCart(
        JSON.stringify({
          id: giftId,
          quantity: 1,
          properties: { _isLiveGift: true },
        }),
        'live-gift'
      );
    } else if (!eligible && hasGift) {
      const giftItem = (cart.items || []).find((item) => MDiscountCodesComponent.isLiveGiftLine(item));
      await AjaxCart.changeCart(
        JSON.stringify({ id: giftItem?.key || giftId, quantity: 0 }),
        'live-gift'
      );
    }
  }

  async syncBirthdayGiftForCart() {
    return MDiscountCodesComponent.syncBirthdayGiftForCart();
  }

  /** Re-render the drawer after a background gift change (code gift added / removed). */
  static refreshDrawerAfterGift() {
    const component = document.querySelector('m-discount-codes-component');
    if (component && typeof component.updateCartUI === 'function') component.updateCartUI();
  }

  /** Codes currently shown as chips, without the theme-handled gift codes. */
  shopifyChipCodes() {
    return MDiscountCodesComponent.withoutGiftCodes(
      Array.from(this.querySelectorAll('.js-remove-discount-button')).map((element) => element.dataset.discountCode)
    );
  }

  showCodeError(code) {
    const errorBox = this.querySelector('.js-discount-error');
    const wrong = this.querySelector('.js-wrong-disc');
    if (wrong) wrong.textContent = `"${code}"`;
    if (errorBox) errorBox.classList.remove('hidden');
  }

  async removeDiscount(e) {
    const removedCode = e.currentTarget.dataset.discountCode;

    // Theme-handled gift code: clear the attribute + gift + code on the Shopify cart.
    const removedGift = MDiscountCodesComponent.codeGiftFor(removedCode);
    if (removedGift) {
      await MDiscountCodesComponent.setGiftCodeAttribute(removedGift, false);
      const cartNow = await AjaxCart.getCart();
      const lines = (cartNow?.items || []).filter((item) => MDiscountCodesComponent.isCodeGiftLine(item, removedGift));
      for (const line of lines) {
        await AjaxCart.changeCart(JSON.stringify({ id: line.key, quantity: 0 }), 'code-gift');
      }
      const cartAfter = await AjaxCart.getCart();
      if (cartAfter) await MDiscountCodesComponent.syncGiftCodeOnShopify(cartAfter, removedGift, false);
      this.updateCartUI();
      return;
    }

    const allCodes = this.shopifyChipCodes();
    const remainingCodes = allCodes.filter((code) => code !== removedCode);
    const codesToApply = remainingCodes.length > 0 ? remainingCodes : null;

    // Stop Birthday Sale from re-applying after a manual cart-drawer removal.
    // Call before GraphQL so queued birthday applies abort before they overwrite.
    if (
      window.BirthdaySalePDP &&
      typeof window.BirthdaySalePDP.handleCartCodeRemoved === 'function'
    ) {
      window.BirthdaySalePDP.handleCartCodeRemoved(removedCode);
    } else if (
      window.BirthdaySalePDP &&
      typeof window.BirthdaySalePDP.dismissCode === 'function' &&
      typeof window.BirthdaySalePDP.isActive === 'function' &&
      window.BirthdaySalePDP.isActive()
    ) {
      window.BirthdaySalePDP.dismissCode();
    }

    // Also clear Shopify discount_code cookie so syncCheckoutDiscounts cannot revive it.
    try {
      document.cookie = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    } catch (e) {}

    await this.applyDiscount(codesToApply);

    // Check if specific discount codes were removed and remove associated products
    if (removedCode.toUpperCase() === 'IMMUN25') {
      await AjaxCart.changeCart(JSON.stringify({ "id": '53775142879500', "quantity": 0 }));
    }
    if (removedCode.toUpperCase() === 'BLACKPALMS25') {
      await AjaxCart.changeCart(JSON.stringify({ "id": '53834937303308', "quantity": 0 }));
    }
    if (removedCode.toUpperCase() === 'LM25') {
      await AjaxCart.changeCart(JSON.stringify({ "id": '54146067923212', "quantity": 0 }));
    }
    if (removedCode.toUpperCase() === 'PREVENTA30') {
      await AjaxCart.changeCart(JSON.stringify({ "id": '54660206756108', "quantity": 0 }));
    }
    if (MDiscountCodesComponent.isBirthdayGiftCode(removedCode)) {
      const stillHasBdayCode = remainingCodes.some((code) =>
        MDiscountCodesComponent.isBirthdayGiftCode(code)
      );
      if (!stillHasBdayCode) {
        await AjaxCart.changeCart(
          JSON.stringify({ id: MDiscountCodesComponent.BDAY_GIFT_VARIANT_ID, quantity: 0 }),
          'bday-gift'
        );
      }
    }
    if (MDiscountCodesComponent.isLiveGiftCode(removedCode)) {
      const stillHasLiveCode = remainingCodes.some((code) =>
        MDiscountCodesComponent.isLiveGiftCode(code)
      );
      if (!stillHasLiveCode) {
        await AjaxCart.changeCart(
          JSON.stringify({ id: MDiscountCodesComponent.LIVE_GIFT_VARIANT_ID, quantity: 0 }),
          'live-gift'
        );
      }
    }

    this.updateCartUI();
  }

  async addDiscount() {
    const discountInput = this.querySelector('.js-discount-input');
    const discountCode = (discountInput.value || '').trim();
    if (discountInput) discountInput.blur();
    if (discountCode) {
      // Theme-handled gift code (BACSTAGE-GIFT, during the Fall Sale): store it on the cart,
      // the gift is added from 99 € cart value.
      const gift = MDiscountCodesComponent.codeGiftFor(discountCode);
      if (gift) {
        const errorBox = this.querySelector('.js-discount-error');
        if (errorBox) errorBox.classList.add('hidden');
        const cartBefore = await AjaxCart.getCart();
        if (gift.oneTimeOnly && !MDiscountCodesComponent.cartHasOneTimeItem(cartBefore, gift)) {
          this.showCodeError(gift.code);
          return;
        }
        await MDiscountCodesComponent.setGiftCodeAttribute(gift, true);
        const cart = await AjaxCart.getCart();
        await MDiscountCodesComponent.syncCodeGiftsOnCart(cart);
        discountInput.value = '';
        discountInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.updateCartUI();
        return;
      }

      // Non-birthday codes should win over Birthday Sale auto-reapply.
      if (
        window.BirthdaySalePDP &&
        typeof window.BirthdaySalePDP.handleCartCodeAdded === 'function'
      ) {
        window.BirthdaySalePDP.handleCartCodeAdded(discountCode);
      }

      let allCodes = this.shopifyChipCodes();
      // Stale DOM can still list a birthday chip after remove; don't merge it back in.
      if (
        window.BirthdaySalePDP &&
        typeof window.BirthdaySalePDP.isCodeDismissed === 'function' &&
        window.BirthdaySalePDP.isCodeDismissed() &&
        typeof window.BirthdaySalePDP.isSegmentDiscountCode === 'function'
      ) {
        allCodes = allCodes.filter(
          (code) => !window.BirthdaySalePDP.isSegmentDiscountCode(code)
        );
      }
      const updatedCodes = [...new Set([...allCodes, discountCode])];
      await this.applyDiscount(updatedCodes);
    } else {
      this.querySelector('.js-discount-error').classList.remove('hidden');
    }
  }

  updateCartUI(isError, nonApplicableDiscounts) {
    fetch(`${routes.cart_url}?section_id=${this.dataset.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.drawer__footer.drawer__wrapper'];

        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        if (isError && nonApplicableDiscounts) {
          document.querySelector('.js-wrong-disc').innerHTML = nonApplicableDiscounts;
          document.querySelector('.js-discount-error').classList.remove('hidden');
        }

        const cartComponent = document.querySelector('cart-items');

        window.drawerAnimatedBar();
        window.refreshTrustpilotWidgets?.(document.getElementById('CartDrawer') || document);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getCookie(name) {
    const cookieArr = document.cookie.split(';');

    for (let i = 0; i < cookieArr.length; i++) {
      const cookiePair = cookieArr[i].split('=');
      if (name.trim() === cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }

    return null;
  }

  applyDiscount(codes) {
    // Gift codes are handled by the theme during the sale (the gift sync puts them back on the
    // Shopify cart while the gift is in the cart).
    if (Array.isArray(codes)) {
      codes = MDiscountCodesComponent.withoutGiftCodes(codes);
      if (!codes.length) codes = null;
    }
    return new Promise(async (resolve, reject) => {
      try {
        const formattedCodes = Array.isArray(codes) ? JSON.stringify(codes) : '[]';
        const query = `
          mutation {
            cartDiscountCodesUpdate(
              cartId: "${this.cartId}", 
              discountCodes: ${formattedCodes}
            ) {
              cart {
                id
                discountCodes {
                  code
                  applicable
                }
                checkoutUrl
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `;

        fetch(this.apiUrl, {
          headers: {
            'Content-Type': 'application/graphql',
            'X-Shopify-Storefront-Access-Token': `${this.storeApiToken}`
          },
          body: query,
          method: 'POST'
        })
          .then((response) => response.json())
          .then(async (data) => {
            const discountCodes = data.data.cartDiscountCodesUpdate.cart.discountCodes;

            // Check if "IMMUN25" is applied and applicable
            const immun25Code = discountCodes.find((code) => code.code.toUpperCase() === 'IMMUN25');
            const isImmun25Applicable = immun25Code ? immun25Code.applicable : false;

            const blackPalms25Code = discountCodes.find((code) => code.code.toUpperCase() === 'BLACKPALMS25');
            const isBlackPalms25Applicable = blackPalms25Code ? blackPalms25Code.applicable : false;

            const preventa30code = discountCodes.find((code) => code.code.toUpperCase() === 'PREVENTA30');
            const isPreventa30Applicable = preventa30code ? preventa30code.applicable : false;

            // Check if "LM25" is applied and applicable
            const LM25Code = discountCodes.find((code) => code.code.toUpperCase() === 'LM25');
            const isLM25Applicable = LM25Code ? LM25Code.applicable : false;

            const hasApplicableBirthdayGiftCode = discountCodes.some(
              (entry) =>
                entry.applicable && MDiscountCodesComponent.isBirthdayGiftCode(entry.code)
            );
            const hasApplicableLiveGiftCode = discountCodes.some(
              (entry) => entry.applicable && MDiscountCodesComponent.isLiveGiftCode(entry.code)
            );

            const isError = discountCodes.some((code) => !code.applicable);
            const allCodes = Array.from(this.querySelectorAll('.js-remove-discount-button')).map((element) =>
              element.dataset.discountCode
            );

            const nonApplicableDiscounts = discountCodes
              .filter((code) => !code.applicable && !allCodes.includes(code.code))
              .map((code) => `"${code.code}"`)
              .join(',');

            // If IMMUN25 is applicable, add a specific product to the cart
            if (isImmun25Applicable) {
              await AjaxCart.addToCart(JSON.stringify({ "id": '53775142879500', "quantity": 1 }));
            }
            if (isBlackPalms25Applicable) {
              await AjaxCart.addToCart(JSON.stringify({ "id": '53834937303308', "quantity": 1 }));
            }
            if (isLM25Applicable) {
              await AjaxCart.addToCart(JSON.stringify({ "id": '54146067923212', "quantity": 1 }));
            }
            if (isPreventa30Applicable) {
              await AjaxCart.addToCart(JSON.stringify({ "id": '54660206756108', "quantity": 1 }));
            }

            // Birthday campaign gift: only when an applicable BDAY* code is on AND cart ≥ €150.
            // BDAY-LIVE35 gift: only when that code is applicable AND cart ≥ €120.
            // Code gifts (BACSTAGE-GIFT): sale running, cart attribute AND cart ≥ minTotal.
            await MDiscountCodesComponent.syncBirthdayGiftForCart({
              forceCodeEligible: hasApplicableBirthdayGiftCode,
              forceLiveCodeEligible: hasApplicableLiveGiftCode,
              skipDrawerRefresh: true,
            });

            this.updateCartUI(isError, nonApplicableDiscounts);
          })
          .finally(() => {
            resolve();
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  requestCallback(requestURL) {
    if ((requestURL.includes('/add.js')
        || requestURL.includes('/update.js')
        || requestURL.includes('/change.js'))
      && !sessionStorage.getItem('discount_token_added')) {

      this.cartToken = this.getCookie('cart');
      this.cartId = `gid://shopify/Cart/${this.cartToken}`;
      sessionStorage.setItem('discount_token_added', 'true');
    }
  }

  networkCallObserver(callback) {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        callback(entry.name);
      });
    });
    observer.observe({ entryTypes: ['resource'] });
  }

  addEventListeners() {
    const discountSubmit = this.querySelector('.js-add-discount-button');
    const discountRemove = this.querySelectorAll('.js-remove-discount-button');

    if (discountSubmit) {
      discountSubmit.addEventListener('click', this.addDiscount.bind(this));
    }

    if (discountRemove.length) {
      discountRemove.forEach(el =>
        el.addEventListener('click', this.removeDiscount.bind(this))
      );
    }
  }

  removeEventListeners() {
    const discountSubmit = this.querySelector('.js-add-discount-button');
    const discountRemove = this.querySelectorAll('.js-remove-discount-button');

    if (discountSubmit) {
      discountSubmit.removeEventListener('click', this.addDiscount.bind(this));
    }

    if (discountRemove.length) {
      discountRemove.forEach(el =>
        el.removeEventListener('click', this.removeDiscount.bind(this))
      );
    }
  }
}

customElements.define('m-discount-codes-component', MDiscountCodesComponent);

// Bind as soon as this asset loads — don't wait for the cart drawer custom element.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => MDiscountCodesComponent.ensureBirthdayGiftCartSync());
} else {
  MDiscountCodesComponent.ensureBirthdayGiftCartSync();
}
